import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import axios, { API } from "@/lib/api";
import StatusBadge from "@/components/shared/StatusBadge";
import LoadingScreen from "@/components/shared/LoadingScreen";
import EmptyState from "@/components/shared/EmptyState";
import { Users } from "lucide-react";

export default function AdminJamaah() {
  const [items, setItems] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [detail, setDetail] = useState(null);

  const load = () => {
    const params = {};
    if (search) params.search = search;
    if (status !== "all") params.status = status;
    axios.get(`${API}/admin/jamaah`, { params }).then(({ data }) => setItems(data)).catch(() => setItems([]));
  };

  useEffect(() => { load(); }, [search, status]);

  const openDetail = (profileId) => {
    axios.get(`${API}/admin/jamaah/${profileId}`).then(({ data }) => setDetail(data));
  };

  return (
    <div data-testid="admin-jamaah-page" className="space-y-5">
      <div>
        <h1 className="font-heading text-2xl font-bold text-[#1F2937]">Data Jamaah</h1>
        <p className="text-sm text-[#64748B]">Kelola data seluruh calon dan jamaah aktif.</p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#64748B]" />
          <Input data-testid="admin-jamaah-search-input" placeholder="Cari nama, NIK, atau HP..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-10 rounded-xl pl-9" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="admin-jamaah-status-filter" className="h-10 w-full rounded-xl sm:w-52"><SelectValue placeholder="Semua Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Status</SelectItem>
            <SelectItem value="prospective">Calon Jamaah</SelectItem>
            <SelectItem value="registered">Terdaftar</SelectItem>
            <SelectItem value="verified">Terverifikasi</SelectItem>
            <SelectItem value="active">Aktif</SelectItem>
            <SelectItem value="completed">Selesai</SelectItem>
            <SelectItem value="cancelled">Dibatalkan</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {items === null ? (
        <LoadingScreen />
      ) : items.length === 0 ? (
        <EmptyState icon={Users} title="Belum ada data jamaah" description="Data jamaah akan muncul setelah ada pendaftaran." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-100 bg-white">
          <Table data-testid="admin-jamaah-table">
            <TableHeader>
              <TableRow>
                <TableHead>Nama</TableHead>
                <TableHead>NIK</TableHead>
                <TableHead>Kota</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Persiapan</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((j) => (
                <TableRow key={j.id} data-testid={`admin-jamaah-row-${j.id}`} className="cursor-pointer hover:bg-slate-50" onClick={() => openDetail(j.id)}>
                  <TableCell className="font-medium text-[#1F2937]">{j.name}</TableCell>
                  <TableCell className="text-[#64748B]">{j.nik}</TableCell>
                  <TableCell className="text-[#64748B]">{j.city}</TableCell>
                  <TableCell><StatusBadge status={j.status} /></TableCell>
                  <TableCell className="text-[#64748B]">{j.preparation_progress || 0}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Sheet open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <SheetContent side="right" className="w-full overflow-y-auto bg-white sm:max-w-lg" data-testid="admin-jamaah-detail-sheet">
          {detail && (
            <>
              <SheetHeader>
                <SheetTitle>{detail.user?.name}</SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <p className="text-[#64748B]">NIK: <span className="text-[#1F2937]">{detail.nik}</span></p>
                  <p className="text-[#64748B]">Status: <StatusBadge status={detail.status} /></p>
                  <p className="text-[#64748B]">Email: <span className="text-[#1F2937]">{detail.user?.email}</span></p>
                  <p className="text-[#64748B]">HP: <span className="text-[#1F2937]">{detail.user?.phone}</span></p>
                  <p className="text-[#64748B] col-span-2">Alamat: <span className="text-[#1F2937]">{detail.address}, {detail.city}, {detail.province}</span></p>
                </div>
                <div>
                  <h4 className="font-heading text-sm font-semibold text-[#1F2937]">Dokumen</h4>
                  {detail.documents.length === 0 ? <p className="mt-1 text-xs text-[#64748B]">Belum ada dokumen</p> : (
                    <div className="mt-2 space-y-2">
                      {detail.documents.map((d) => (
                        <div key={d.id} className="flex items-center justify-between rounded-lg bg-[#F5F9F7] px-3 py-2">
                          <span className="uppercase text-xs font-medium text-[#1F2937]">{d.type}</span>
                          <StatusBadge status={d.status} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <h4 className="font-heading text-sm font-semibold text-[#1F2937]">Pembayaran</h4>
                  {detail.payments.length === 0 ? <p className="mt-1 text-xs text-[#64748B]">Belum ada data pembayaran</p> : (
                    <div className="mt-2 space-y-2">
                      {detail.payments.map((p) => (
                        <div key={p.id} className="flex items-center justify-between rounded-lg bg-[#F5F9F7] px-3 py-2">
                          <span className="text-xs text-[#1F2937]">Rp {p.amount_paid?.toLocaleString("id-ID")} / Rp {p.total_fee?.toLocaleString("id-ID")}</span>
                          <StatusBadge status={p.status} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
